import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePlayerStore from '../store/playerStore';
import PlaylistPicker from './PlaylistPicker';

function formatTime(s) {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

function QueueRow({ track, status, onDelete, onJump, onDragStart, onDragOver, onDrop, isDragging }) {
  const isHistory = status === 'history';
  const isCurrent = status === 'current';
  const isUpcoming = status === 'upcoming';

  return (
    <div
      draggable={isUpcoming}
      onDragStart={isUpcoming ? onDragStart : undefined}
      onDragOver={isUpcoming ? (e) => { e.preventDefault(); onDragOver(); } : undefined}
      onDrop={isUpcoming ? onDrop : undefined}
      onClick={() => !isHistory && onJump()}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        borderRadius: 8,
        cursor: isHistory ? 'default' : isUpcoming ? 'grab' : 'pointer',
        background: isCurrent
          ? 'rgba(105,190,40,0.2)'
          : isDragging
          ? 'rgba(255,255,255,0.1)'
          : 'transparent',
        border: isCurrent
          ? '1px solid rgba(105,190,40,0.4)'
          : isDragging
          ? '1px dashed rgba(255,255,255,0.3)'
          : '1px solid transparent',
        opacity: isHistory ? 0.4 : 1,
        transition: 'background 0.15s, border 0.15s',
        marginBottom: 4,
      }}
      onMouseEnter={e => { if (!isCurrent && !isHistory && !isDragging) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
      onMouseLeave={e => { if (!isCurrent && !isHistory) e.currentTarget.style.background = isCurrent ? 'rgba(105,190,40,0.2)' : 'transparent'; }}
    >
      {/* Drag handle — only for upcoming */}
      {isUpcoming && (
        <div style={{
          flexShrink: 0, color: 'rgba(255,255,255,0.2)',
          fontSize: 16, cursor: 'grab', userSelect: 'none',
          paddingRight: 4,
        }}>
          ⠿
        </div>
      )}

      {/* Album art thumbnail */}
      <div style={{ flexShrink: 0 }}>
        {track.cover_art_url
          ? <img src={track.cover_art_url} alt="" style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }} />
          : <div style={{ width: 40, height: 40, borderRadius: 4, background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🎵</div>
        }
      </div>

      {/* Track info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: isCurrent ? 700 : 500,
          color: isCurrent ? '#69BE28' : '#fff',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {isCurrent && '♪ '}{track.title}
        </div>
        <div style={{
          fontSize: 11, color: 'rgba(255,255,255,0.5)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {track.artist_name} · {track.album_title}{track.year ? ` · ${track.year}` : ''}
        </div>
      </div>

      {/* Delete button — only for upcoming */}
      {isUpcoming && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{
            flexShrink: 0,
            background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.3)',
            fontSize: 16, cursor: 'pointer', padding: '2px 6px',
            borderRadius: 4,
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#ff6b6b'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}
        >
          ✕
        </button>
      )}
    </div>
  );
}

export default function FullScreenPlayer({ progress, duration, onSeek, onExit }) {
const {
  currentTrack, isPlaying, shuffle, repeat, queue, currentIndex, history,
  togglePlay, next, prev, toggleShuffle, toggleRepeat,
  removeFromQueue, jumpToTrack, reorderQueue, volume, setVolume,
} = usePlayerStore();

  const navigate = useNavigate();
  const [showQueue, setShowQueue] = useState(false);
  const [pickerPos, setPickerPos] = useState(null);
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const currentRowRef = useRef(null);

  // Enter native fullscreen when component mounts
  useEffect(() => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  // Escape key + spacebar handler
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        onExit();
        return;
      }
      if (e.code === 'Space') {
        const tag = e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
        e.preventDefault();
        togglePlay();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onExit, togglePlay]);

  // Auto-scroll queue to current track
  useEffect(() => {
    if (showQueue && currentRowRef.current) {
      currentRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentIndex, showQueue]);

  if (!currentTrack) return null;

  const goToAlbum = () => {
    onExit();
    navigate(`/albums/${currentTrack.album}`);
  };

  const upcomingTracks = queue.slice(currentIndex + 1, currentIndex + 14);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: '#000',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Top bar — App View + Upcoming toggle */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        flexShrink: 0,
        paddingTop: 'max(12px, env(safe-area-inset-top))',
      }}>
        <button onClick={onExit} style={overlayBtnStyle({})}>← App View</button>
        <button onClick={() => setShowQueue(q => !q)} style={overlayBtnStyle({})}>
          {showQueue ? '⛶ Full View' : '≡ Show Upcoming'}
        </button>
      </div>

      <div style={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        alignItems: 'stretch',
      }}>
        {/* Left — art + controls */}
        <div style={{
          flex: showQueue ? '0 0 60%' : '1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'flex 0.3s ease',
          minWidth: 0,
        }}>
          {/* Album art */}
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            padding: '20px',
            minHeight: 0,
          }}>
            {currentTrack.cover_art_url
              ? <img
                  src={currentTrack.cover_art_url}
                  alt=""
                  onClick={goToAlbum}
                  style={{
                    height: '100%',
                    width: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: 12,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
                    cursor: 'pointer',
                  }}
                />
              : <div
                  onClick={goToAlbum}
                  style={{
                    width: '100%', height: '100%', borderRadius: 12,
                    background: '#1a1a1a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 80, cursor: 'pointer',
                  }}>🎵</div>
            }
          </div>

          {/* Track info */}
          <div style={{ textAlign: 'center', padding: '24px 40px 8px', width: '100%', maxWidth: 600 }}>
            <div onClick={goToAlbum} style={{ fontSize: 24, fontWeight: 700, color: '#fff', marginBottom: 4, cursor: 'pointer' }}>
              {currentTrack.title}
            </div>
            <div
              onClick={goToAlbum}
              style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}
            >
              {currentTrack.artist_name} — {currentTrack.album_title}{currentTrack.year ? ` — ${currentTrack.year}` : ''}
            </div>
          </div>

          {/* Progress bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            width: '100%', maxWidth: 600, padding: '0 40px',
          }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', minWidth: 36 }}>
              {formatTime(progress)}
            </span>
            <input
              type="range" min="0" max="100"
              value={duration ? (progress / duration) * 100 : 0}
              onChange={onSeek}
              style={{ flex: 1, accentColor: '#69BE28' }}
            />
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', minWidth: 36, textAlign: 'right' }}>
              {formatTime(duration)}
            </span>
          </div>

          {/* Controls */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 24,
            padding: '16px 40px 40px',
          }}>
            <button onClick={prev} style={btnStyle()}>⏮</button>
            <button onClick={togglePlay} style={{ ...btnStyle(), fontSize: 48 }}>
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button onClick={next} style={btnStyle()}>⏭</button>
            <button
              onClick={(e) => {
                if (window.innerWidth > 768) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setPickerPos({ x: rect.left, y: rect.bottom + 4 });
                } else {
                  setPickerPos({});
                }
              }}
              style={{
                ...btnStyle(),
                fontSize: 22,
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 8,
                padding: '6px 12px',
              }}
              title="Add to playlist"
            >
              ＋
            </button>
            {/* Volume — hidden on mobile (use system volume controls) */}
            <div className="fullscreen-volume">
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 18 }}>🔊</span>
              <input
                type="range" min="0" max="1" step="0.02"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                style={{ width: 80, accentColor: '#69BE28' }}
              />
            </div>
          </div>
        </div>

        {/* Add to playlist picker */}
        {pickerPos !== null && (
          <PlaylistPicker
            track={currentTrack}
            position={pickerPos.x !== undefined ? pickerPos : undefined}
            onClose={() => setPickerPos(null)}
          />
        )}

        {/* Right — Queue panel */}
        {showQueue && (
          <div style={{
            flex: '0 0 40%',
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid rgba(255,255,255,0.1)',
            padding: '24px 16px',
            minWidth: 0,
          }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase', letterSpacing: '0.1em',
              marginBottom: 16, paddingLeft: 12,
            }}>
              Up Next
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {/* History */}
              {history.map((track, i) => (
                <QueueRow
                  key={`history-${i}`}
                  track={track}
                  status="history"
                  onDelete={() => {}}
                  onJump={() => {}}
                />
              ))}

              {/* Current */}
              <div ref={currentRowRef}>
                <QueueRow
                  track={currentTrack}
                  status="current"
                  onDelete={() => {}}
                  onJump={() => {}}
                />
              </div>

              {/* Upcoming */}
              {upcomingTracks.map((track, i) => {
                const queueIndex = currentIndex + 1 + i;
                return (
                  <QueueRow
                    key={`upcoming-${queueIndex}`}
                    track={track}
                    status="upcoming"
                    isDragging={dragOverIndex === queueIndex}
                    onDelete={() => removeFromQueue(queueIndex)}
                    onJump={() => jumpToTrack(queueIndex)}
                    onDragStart={() => setDragFromIndex(queueIndex)}
                    onDragOver={() => setDragOverIndex(queueIndex)}
                    onDrop={() => {
                      if (dragFromIndex !== null && dragOverIndex !== null && dragFromIndex !== dragOverIndex) {
                        reorderQueue(dragFromIndex, dragOverIndex);
                      }
                      setDragFromIndex(null);
                      setDragOverIndex(null);
                    }}
                  />
                );
              })}

              {upcomingTracks.length === 0 && (
                <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, padding: '12px', textAlign: 'center' }}>
                  No upcoming tracks
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

const btnStyle = (active) => ({
  background: 'none',
  border: 'none',
  color: active ? '#69BE28' : '#fff',
  fontSize: 28,
  cursor: 'pointer',
  opacity: active ? 1 : 0.8,
  padding: 8,
});

const overlayBtnStyle = () => ({
  background: 'rgba(255,255,255,0.1)',
  border: '1px solid rgba(255,255,255,0.2)',
  color: '#fff', borderRadius: 8,
  padding: '8px 16px', fontSize: 13,
  cursor: 'pointer',
});
